import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table/table";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import type { RenewalFamily } from "@/types/renewals";

import { statusLabel, statusVariant } from "../constants";
import { actionLabel } from "../renewal-format";
import { formatGbp } from "@/views/spend/money-format";

interface Props {
  families: RenewalFamily[];
  ownerLabel: (ownerUserId: string | null) => string | null;
}

export const RenewalTable = ({ families, ownerLabel }: Props) => {
  const { formatDate } = useDateFormatter();

  return (
    <section className="mt-5 overflow-hidden rounded-lg border border-[#ddd5cb] bg-[#fffdfb]">
      <Table aria-label="Contract renewals">
        <TableHeader>
          <TableRow>
            <TableHead>Contract Family / Supplier</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Term end</TableHead>
            <TableHead>Termination</TableHead>
            <TableHead>Notice due</TableHead>
            <TableHead className="text-right">Total contract value (GBP)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {families.map(family => (
            <TableRow key={family.relation_id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/renewal-centre/${encodeURIComponent(family.relation_id)}`}
                    className="font-semibold text-[#111b35] hover:underline"
                  >
                    {family.family_name}
                  </Link>
                  {family.procurement_status ? (
                    <Badge
                      variant={statusVariant(family.procurement_status)}
                      className="shrink-0 uppercase tracking-wide"
                    >
                      {statusLabel(family.procurement_status)}
                    </Badge>
                  ) : null}
                </div>
                <p className="text-xs text-gray-500">{family.supplier_name}</p>
              </TableCell>
              <TableCell>
                {family.category_l3 ? (
                  <Badge variant="outline" className="font-medium text-gray-600">
                    {family.category_l3}
                  </Badge>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell className="text-sm text-gray-600">{ownerLabel(family.owner_user_id) ?? "—"}</TableCell>
              <TableCell className="text-sm tabular-nums">
                {family.term_end_date ? formatDate(family.term_end_date) : "—"}
              </TableCell>
              <TableCell>
                {family.termination_type ? (
                  <Badge variant="outline" className="border-sky-200 bg-sky-50 font-medium text-sky-800">
                    {family.termination_type}
                  </Badge>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell className="text-sm tabular-nums">{actionLabel(family.days_to_action)}</TableCell>
              <TableCell className="text-right text-sm font-semibold tabular-nums">
                {formatGbp(family.total_contract_value_gbp) ?? "Not available"}
              </TableCell>
            </TableRow>
          ))}
          {families.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="py-8 text-center text-sm text-gray-500">
                No Contract Families match these filters.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </section>
  );
};
